import React, { ReactNode } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from 'react-error-boundary';

interface ErrorBoundaryProps {
  children: ReactNode;
}

/**
 * Default error fallback component
 */
function DefaultErrorFallback({ error, resetErrorBoundary }: FallbackProps): JSX.Element {
  const errorMessage = error instanceof Error ? error.toString() : String(error);

  return (
    <div style={{
      padding: '20px',
      margin: '20px',
      border: '1px solid #ff6b6b',
      borderRadius: '8px',
      backgroundColor: '#ffe0e0',
      color: '#c92a2a',
    }}>
      <h2>Something went wrong</h2>
      <p>We're sorry, but something unexpected happened.</p>
      <details style={{ marginTop: '10px', cursor: 'pointer' }}>
        <summary>Error details</summary>
        <pre style={{
          marginTop: '10px',
          padding: '10px',
          backgroundColor: '#fff',
          border: '1px solid #ddd',
          borderRadius: '4px',
          overflow: 'auto',
          fontSize: '12px',
        }}>
          {errorMessage}
        </pre>
      </details>
      <button
        onClick={resetErrorBoundary}
        style={{
          marginTop: '15px',
          padding: '10px 20px',
          backgroundColor: '#c92a2a',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
        }}
      >
        Try again
      </button>
    </div>
  );
}

/**
 * Error handler that reports errors to Datadog
 */
function handleError(error: unknown, info: React.ErrorInfo): void {
  // Log error to console in development
  console.error('ErrorBoundary caught an error:', error, info);

  // Report to Datadog RUM
  if (datadogRum && error instanceof Error) {
    datadogRum.addError(error, {
      type: 'react_error',
      componentStack: info.componentStack || '',
      errorBoundary: true,
    });
  }
}

/**
 * ErrorBoundary component that catches React errors and reports them to Datadog
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
function ErrorBoundary({ children }: ErrorBoundaryProps): JSX.Element {
  return (
    <ReactErrorBoundary
      FallbackComponent={DefaultErrorFallback}
      onError={handleError}
    >
      {children}
    </ReactErrorBoundary>
  );
}

export default ErrorBoundary;

